import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { ActivityAssociationInterface, ActivityInterface } from '@vendasta/crm';
import { Opportunity } from '@vendasta/sales-opportunities';
import { Observable, ReplaySubject, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs';
import {
  ObjectAssociationType,
  ObjectType,
  ActivitiesTimelineService,
  CrmTimelineComponent,
  PrimaryAccountGroupIDForCrmObject,
} from '@galaxy/crm/static';

type Mode = 'view' | 'add';

@Component({
  selector: 'sales-ui-opportunity-activities',
  templateUrl: './opportunity-activities.component.html',
  styleUrls: ['./opportunity-activities.component.scss'],
  providers: [
    {
      provide: PrimaryAccountGroupIDForCrmObject,
      useFactory: (comp: OpportunityActivitiesComponent) => {
        return comp.opportunity$$.pipe(
          map((opportunity) => opportunity?.accountGroupId),
          filter((id) => Boolean(id)),
          shareReplay(1),
        );
      },
      deps: [OpportunityActivitiesComponent],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpportunityActivitiesComponent {
  @ViewChild('scrollableContainerRef', { static: true }) scrollableContainer!: ElementRef;
  @ViewChild('crmTimelineRef') timeline!: CrmTimelineComponent;

  @Input() set opportunity(value: Opportunity) {
    this.opportunity$$.next(value);
  }

  companyId$: Observable<string>;
  opportunityId$: Observable<string>;
  extraAssociations$: Observable<ActivityAssociationInterface[]>;
  mode: Mode = 'view';
  showLoadMore = false;
  private scrollTop = 0;
  readonly crmAssociationType: ObjectAssociationType = 'Opportunity';
  readonly crmObjectType: ObjectType = 'Company';
  readonly opportunity$$ = new ReplaySubject<Opportunity>(1);

  constructor(
    private readonly accountGroupService: AccountGroupService,
    private readonly activitiesTimelineService: ActivitiesTimelineService,
  ) {
    this.activitiesTimelineService.initializeState('', []);
    this.setupSubscriptions();
  }

  setMode(mode: Mode): void {
    this.mode = mode;
  }

  private setupSubscriptions(): void {
    this.extraAssociations$ = this.opportunity$$.pipe(
      map((opportunity) => opportunity?.opportunityId),
      filter((id) => Boolean(id)),
      distinctUntilChanged(),
      map((crmObjectId) => [{ crmObjectId, crmObjectType: this.crmAssociationType }]),
    );
    const accountGroup$ = this.opportunity$$.pipe(
      map((opportunity) => opportunity?.accountGroupId),
      filter((id) => Boolean(id)),
      distinctUntilChanged(),
      switchMap((id) =>
        this.accountGroupService.get(id, new ProjectionFilter({ accountGroupExternalIdentifiers: true })),
      ),
      shareReplay(1),
    );
    this.opportunityId$ = this.opportunity$$.pipe(map((opportunity) => opportunity?.opportunityId));
    this.companyId$ = accountGroup$.pipe(
      // TODO: add `platformCrmCompanyId` identifier
      map((accountGroup) => accountGroup.externalIdentifiers['platformCrmCompanyId']),
    );
  }

  addActivity(activity: ActivityInterface): void {
    this.timeline.addActivity(activity);
    this.setMode('view');
  }

  handleActivitiesChanged(activities: ActivityInterface[]): void {
    this.scrollableContainer.nativeElement.scrollTop = this.scrollTop;
    this.showLoadMore = activities?.length > 0;
  }

  loadMoreActivities(): void {
    this.scrollTop = this.scrollableContainer?.nativeElement?.scrollTop ?? 0;
    this.timeline?.loadMoreActivities();
  }
}
